import type {
  Challenge,
  Club,
  Competition,
  MarketplaceItem,
  Post,
  PostAuthor,
  RewardBalance,
  RewardHistory,
} from "../models";
import { dateOnly, daysFromToday } from "../mockDate";
import { CURRENT_USER_AUTHOR, authorForId } from "./people";
import { seededBool, seededInt } from "./random";

/**
 * Ports of the smaller mocks: challenges and competitions, rewards, the
 * marketplace, clubs, and media upload.
 *
 * `MockEventService` and `MockMarketplaceService` pinned absolute ISO dates
 * ("2026-03-04T10:00:00Z") instead of using MockDate offsets like the rest of
 * the seed. Those went stale the moment the calendar moved past them — club
 * meetings landing in the past, listings posted five months ago. Converted to
 * offsets so the fixture ages with the demo.
 */

const delay = (seconds: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, seconds * 1000));

/* ---------------- Challenges & competitions ---------------- */

export class MockChallengeService {
  async getTodayChallenge(): Promise<Challenge> {
    await delay(0.3);
    return {
      id: "challenge-today",
      prompt: "Show us your best study spot on campus!",
      date: dateOnly(0),
      isActive: true,
      submissionCount: 47,
      expiresAt: daysFromToday(0, 23, 59),
    };
  }

  async getChallengeSubmissions(): Promise<Post[]> {
    await delay(0.3);
    return [];
  }

  async submitChallenge(_challengeId: string, videoURL: string): Promise<Post> {
    await delay(0.5);
    return {
      id: `post-challenge-${crypto.randomUUID()}`,
      content: "My challenge submission!",
      mediaURL: videoURL,
      mediaType: "video",
      likeCount: 0,
      commentCount: 0,
      shareCount: 0,
      viewCount: 0,
      isLikedByMe: false,
      isSavedByMe: false,
      author: CURRENT_USER_AUTHOR,
      hashtags: ["#challenge"],
      createdAt: daysFromToday(0, 12, 0),
      postType: "regular",
    };
  }

  async getCompetitions(): Promise<Competition[]> {
    await delay(0.3);
    return [
      {
        id: "best-dorm-glow-up",
        title: "Best Dorm Room Glow-Up",
        entryCount: 23,
        createdAt: daysFromToday(-5, 10, 0),
      },
      {
        id: "funniest-dining-hall",
        title: "Funniest Dining Hall Moment",
        entryCount: 89,
        createdAt: daysFromToday(-2, 15, 0),
      },
      {
        id: "campus-sunset",
        title: "Best Campus Sunset",
        entryCount: 41,
        createdAt: daysFromToday(-4, 8, 0),
      },
    ];
  }

  async getCompetitionFeed(competitionId: string, page: number, limit: number): Promise<Post[]> {
    await delay(0.3);
    const authors = [authorForId("u1"), authorForId("u2"), authorForId("u5")];
    return Array.from({ length: Math.min(limit, 5) }, (_, i) => {
      const idx = page * limit + i;
      const id = `comp-post-${competitionId}-${idx}`;
      return {
        id,
        content: `My entry for this competition! #${competitionId}`,
        mediaType: "photo",
        thumbnailURL: `https://picsum.photos/seed/comp${competitionId}${idx}/400/600`,
        likeCount: seededInt(`${id}-like`, 5, 200),
        commentCount: seededInt(`${id}-comment`, 0, 30),
        shareCount: seededInt(`${id}-share`, 0, 15),
        viewCount: seededInt(`${id}-view`, 50, 1000),
        isLikedByMe: seededBool(`${id}-liked`),
        isSavedByMe: false,
        author: authors[idx % authors.length],
        hashtags: [`#${competitionId}`],
        competitionId,
        createdAt: daysFromToday(-(idx + 1), 12, 0),
        postType: "regular" as const,
      };
    });
  }

  async getLeaderboard(): Promise<{ user: PostAuthor; points: number }[]> {
    await delay(0.3);
    return [
      { user: authorForId("u1"), points: 3200 },
      { user: authorForId("u5"), points: 2800 },
      { user: authorForId("u2"), points: 2650 },
      { user: CURRENT_USER_AUTHOR, points: 1250 },
      { user: authorForId("u4"), points: 980 },
    ];
  }
}

/* ---------------- Rewards ---------------- */

/**
 * Campus perks. Never presented as tokens, coins, or a wallet anywhere in the
 * UI — hard product constraint, and the reason these are "points" and "perks"
 * in every string below.
 */
export class MockRewardService {
  async getBalance(): Promise<RewardBalance> {
    await delay(0.2);
    return { balance: 1250, rank: 4, campusRank: 2 };
  }

  async getHistory(): Promise<RewardHistory[]> {
    await delay(0.3);
    return [
      {
        id: "rh1",
        amount: 50,
        reason: "Completed daily challenge",
        earnedAt: daysFromToday(0, 12, 0),
      },
      {
        id: "rh2",
        amount: 25,
        reason: "Shared lecture notes for CS 101",
        earnedAt: daysFromToday(-1, 15, 0),
      },
      {
        id: "rh3",
        amount: 100,
        reason: "Won Best Dorm Room competition (3rd place)",
        earnedAt: daysFromToday(-2, 10, 0),
      },
      {
        id: "rh4",
        amount: 10,
        reason: "Attended CS 101 lecture",
        earnedAt: daysFromToday(-2, 10, 50),
      },
      {
        id: "rh5",
        amount: 15,
        reason: "Post-class Froshbot check-in",
        earnedAt: daysFromToday(-2, 11, 0),
      },
      {
        id: "rh6",
        amount: 30,
        reason: "Completed assignment on time",
        earnedAt: daysFromToday(-3, 23, 0),
      },
    ];
  }
}

/* ---------------- Marketplace ---------------- */

export class MockMarketplaceService {
  private listings: MarketplaceItem[] = [
    {
      id: "mp1",
      title: "Calculus Textbook (Stewart, 8th Ed)",
      description: "Barely used, no highlights. Includes online access code (unused).",
      price: 45.0,
      category: "Textbooks",
      imageURLs: [],
      seller: authorForId("u3"),
      campus: "UT Austin",
      isSold: false,
      createdAt: daysFromToday(-1, 10, 0),
    },
    {
      id: "mp2",
      title: "Mini Fridge",
      description: "Works perfectly, just upgraded",
      price: 80.0,
      category: "Dorm",
      imageURLs: [],
      seller: authorForId("u1"),
      campus: "UT Austin",
      isSold: false,
      createdAt: daysFromToday(-2, 14, 0),
    },
  ];

  async getListings(category?: string): Promise<MarketplaceItem[]> {
    await delay(0.3);
    return category === undefined
      ? this.listings
      : this.listings.filter((l) => l.category === category);
  }

  async getListing(id: string): Promise<MarketplaceItem> {
    await delay(0.2);
    const listing = this.listings.find((l) => l.id === id);
    if (!listing) throw new Error(`Unknown listing: ${id}`);
    return listing;
  }

  async createListing(input: {
    title: string;
    description: string;
    price: number;
    category: string;
    imageURLs: string[];
  }): Promise<MarketplaceItem> {
    await delay(0.5);
    const item: MarketplaceItem = {
      id: `mp-${crypto.randomUUID()}`,
      ...input,
      seller: CURRENT_USER_AUTHOR,
      campus: "UT Austin",
      isSold: false,
      createdAt: daysFromToday(0, 12, 0),
    };
    this.listings = [item, ...this.listings];
    return item;
  }

  async deleteListing(id: string): Promise<void> {
    await delay(0.3);
    this.listings = this.listings.filter((l) => l.id !== id);
  }
}

/* ---------------- Clubs ---------------- */

export class MockEventService {
  private clubs: Club[] = [
    {
      id: "club1",
      name: "ACM - Computer Science Club",
      description:
        "The largest CS organization on campus. Weekly talks, hackathons, and study sessions.",
      memberCount: 120,
      isMember: true,
      nextMeeting: daysFromToday(2, 17, 0),
      groupChatId: "gc-club1",
    },
    {
      id: "club2",
      name: "Freshman Council",
      description:
        "Your voice in student government. Plan events, advocate for freshman interests.",
      memberCount: 35,
      isMember: false,
      nextMeeting: daysFromToday(4, 18, 0),
    },
    {
      id: "club3",
      name: "Intramural Sports",
      description: "Flag football, basketball, volleyball, and more. All skill levels welcome.",
      memberCount: 200,
      isMember: false,
    },
  ];

  async getClubs(): Promise<Club[]> {
    await delay(0.3);
    return this.clubs;
  }

  async getClub(clubId: string): Promise<Club> {
    await delay(0.3);
    const club = this.clubs.find((c) => c.id === clubId);
    if (!club) throw new Error(`Unknown club: ${clubId}`);
    return club;
  }

  async joinClub(clubId: string): Promise<void> {
    await delay(0.3);
    const club = this.clubs.find((c) => c.id === clubId);
    if (club && !club.isMember) {
      club.isMember = true;
      club.memberCount += 1;
    }
  }

  async leaveClub(clubId: string): Promise<void> {
    await delay(0.3);
    const club = this.clubs.find((c) => c.id === clubId);
    if (club && club.isMember) {
      club.isMember = false;
      club.memberCount -= 1;
    }
  }
}

/* ---------------- Media ---------------- */

export class MockMediaService {
  async uploadImage(_file: Blob): Promise<string> {
    await delay(1.0);
    return `https://cdn.freshman.app/images/mock-${crypto.randomUUID()}.jpg`;
  }

  async uploadVideo(_file: Blob): Promise<string> {
    await delay(2.0);
    return `https://cdn.freshman.app/videos/mock-${crypto.randomUUID()}.mp4`;
  }
}
